/* Report Generator — pure calc, no UI, no localStorage/Supabase reads
   inside (data passed in as params), same convention as
   seasonComparisonCalc.js / profitCalc.js.

   Arturo, 2026-09-17: a Xero-style report generator — pick a report
   type, an entity (supplier or client), a period, detail level,
   status, and which columns to include, then generate one compact
   Excel file. This file builds the row data for each report type;
   ReportGenerator.js (the UI) and reportGeneratorExport.js (the
   Excel writer) consume it. */

import { calcLotProfit, buildExpenseByLot } from "./profitCalc";
import { getISOWeekKey, getMonthKey, formatPeriodLabel } from "./periodHelpers";
import { deriveDeliveryStatus, resolvePendingStage } from "./saleStatusCalc";
import { resolveSeasonId } from "./seasonFilter";

/* Delivery/sale status labels — same wording ShipmentRegistry.js's
   SALE column already uses (SS_LABELS_EN/ES), duplicated here per
   this file's "no UI imports" convention (reportGeneratorCalc.js
   stays pure calc, no component-file imports). */
var SS_LABELS_EN = { goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection", pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery" };
var SS_LABELS_ES = { goodDelivery: "Buena Entrega", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial", pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega" };
function ssLabel(key, lang) { if (!key) return ""; return (lang === "es" ? SS_LABELS_ES : SS_LABELS_EN)[key] || key; }

/* Same bg/text/border palette ShipmentRegistry.js's own SALE column
   badge already uses — exported so ReportGenerator.js (on-screen
   preview) and reportGeneratorExport.js (PDF) can render the Status
   column as a colored badge instead of plain text. 2026-09-23,
   Arturo: "bien pero muestra los colores en el status tambien". */
export var STATUS_COLORS = {
  goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" },
  partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" },
  pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" },
  pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" },
};

/* Per-lot delivery status, computed live — REGLA DE STATUS DE PALLET:
   never a stored value, always derived from the invoices actually
   linked to that lot, same exact logic as ShipmentRegistry.js's
   saleStatusByLot (deriveDeliveryStatus over every invoice line for
   the lot, falling back to resolvePendingStage when nothing's
   confirmed yet). 2026-09-23, Arturo: "quiero agregar poder agregar
   la columna status y que muestre protection, partial portection etx"
   on the Shipments report. */
function computeSaleStatusByLot(invoices, slPassingIndex) {
  var linesByLot = {};
  (invoices || []).forEach(function (inv) {
    (inv.lines || []).forEach(function (l) {
      var lot = l.lot || inv.lot;
      if (!lot) return;
      (linesByLot[String(lot)] || (linesByLot[String(lot)] = [])).push(l);
    });
  });
  var map = {};
  Object.keys(linesByLot).forEach(function (lot) {
    var lotInvoices = (invoices || []).filter(function (inv) { return String(inv.lot) === lot; });
    var derived = deriveDeliveryStatus(linesByLot[lot]);
    var worst = derived || resolvePendingStage(lotInvoices, slPassingIndex);
    if (worst) map[lot] = worst;
  });
  return map;
}

var DISPOSITION_STATUSES = ["dumped", "donated", "samples", "transfer"];

/* ======================== Column definitions ========================
   Single source of truth for BOTH the checkbox picker (ReportGenerator.js)
   and which keys get pulled into the exported row — never drifts out
   of sync. `sum: true` columns get summed when rows are grouped by
   period (see groupRows below) AND feed the grand-total row (see
   computeGrandTotal below). `type` drives formatting in the exporters:
   "money" gets a currency number format + right alignment (Excel) /
   right-aligned monospace text (PDF); "number" gets thousands-
   separator formatting + right alignment; "text" stays left-aligned.
   Arturo, 2026-09-17: "dale formato de moneda a las filas de dinero...
   alineadas de manera contable hacia la derecha... agrega un total
   general sumando cajas y monto". */
export var REPORT_COLUMNS = {
  shipments: {
    hasDetailToggle: true,
    hasStatusFilter: false,
    global: [
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "supplierName", label: { en: "Shipper", es: "Shipper" }, sum: false, type: "text" },
      { key: "shipperInvoice", label: { en: "Shipper Invoice #", es: "Factura del Exportador #" }, sum: false, type: "text" },
      { key: "proforma", label: { en: "Proforma #", es: "Proforma #" }, sum: false, type: "text" },
      { key: "product", label: { en: "Product", es: "Producto" }, sum: false, type: "text" },
      { key: "category", label: { en: "Category", es: "Categoría" }, sum: false, type: "text" },
      { key: "cases", label: { en: "Cases", es: "Cajas" }, sum: true, type: "number" },
      { key: "pallets", label: { en: "Pallets", es: "Pallets" }, sum: true, type: "number" },
      { key: "purchasePrice", label: { en: "Purchase Price", es: "Precio Compra" }, sum: false, type: "money" },
      { key: "total", label: { en: "Total", es: "Total" }, sum: true, type: "money" },
      /* 2026-09-23, Arturo: "quiero agregar poder agregar la columna
         status y que muestre protection, partial portection etx" —
         read-only, computed live per lot (see computeSaleStatusByLot
         above), never a manual pick (REGLA DE STATUS DE PALLET —
         only the Invoice's Pallet Allocations can ever set this). */
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
    /* Arturo, 2026-09-17: "supongo el detail incluye gastos detallados
       por embarque" — yes: Detail adds the shipment's fee/expense
       breakdown to the product rows — both what's CHARGED to the
       shipper (Bill fees for that lot) and what GFI actually PAYS
       vendors (Expense Bill costs for that lot, via profitCalc.js's
       shared buildExpenseByLot — same 3-mechanism lot attribution
       calcLotProfit already relies on, not re-derived here). */
    detail: [
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "supplierName", label: { en: "Shipper", es: "Shipper" }, sum: false, type: "text" },
      { key: "shipperInvoice", label: { en: "Shipper Invoice #", es: "Factura del Exportador #" }, sum: false, type: "text" },
      { key: "proforma", label: { en: "Proforma #", es: "Proforma #" }, sum: false, type: "text" },
      { key: "lineType", label: { en: "Line Type", es: "Tipo de Línea" }, sum: false, type: "text" },
      { key: "description", label: { en: "Description", es: "Descripción" }, sum: false, type: "text" },
      { key: "qty", label: { en: "Qty", es: "Cant." }, sum: true, type: "number" },
      { key: "rate", label: { en: "Rate", es: "Tarifa" }, sum: false, type: "money" },
      { key: "amount", label: { en: "Amount", es: "Monto" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
  },
  bills: {
    hasDetailToggle: true,
    hasStatusFilter: true,
    detail: [
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "supplierName", label: { en: "Shipper", es: "Shipper" }, sum: false, type: "text" },
      { key: "shipperInvoice", label: { en: "Shipper Invoice #", es: "Factura del Exportador #" }, sum: false, type: "text" },
      { key: "proforma", label: { en: "Proforma #", es: "Proforma #" }, sum: false, type: "text" },
      { key: "lineType", label: { en: "Line Type", es: "Tipo de Línea" }, sum: false, type: "text" },
      { key: "description", label: { en: "Description", es: "Descripción" }, sum: false, type: "text" },
      { key: "qty", label: { en: "Qty", es: "Cant." }, sum: true, type: "number" },
      { key: "rate", label: { en: "Rate", es: "Tarifa" }, sum: false, type: "money" },
      { key: "amount", label: { en: "Amount", es: "Monto" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
    global: [
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "supplierName", label: { en: "Shipper", es: "Shipper" }, sum: false, type: "text" },
      { key: "shipperInvoice", label: { en: "Shipper Invoice #", es: "Factura del Exportador #" }, sum: false, type: "text" },
      { key: "proforma", label: { en: "Proforma #", es: "Proforma #" }, sum: false, type: "text" },
      { key: "grossSales", label: { en: "Gross Sales", es: "Venta Bruta" }, sum: true, type: "money" },
      { key: "commission", label: { en: "GFI Commission", es: "Comisión GFI" }, sum: true, type: "money" },
      { key: "totalFees", label: { en: "Total Fees", es: "Total de Cargos" }, sum: true, type: "money" },
      { key: "totalCredits", label: { en: "Total Credits", es: "Total Créditos" }, sum: true, type: "money" },
      { key: "subtotal", label: { en: "Amount Due to Shipper", es: "Monto a Pagar" }, sum: true, type: "money" },
      { key: "advancesPaid", label: { en: "Advances Paid", es: "Adelantos Pagados" }, sum: true, type: "money" },
      { key: "balance", label: { en: "Balance", es: "Saldo" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
  },
  expenseBills: {
    hasDetailToggle: true,
    hasStatusFilter: true,
    detail: [
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "vendor", label: { en: "Vendor", es: "Proveedor" }, sum: false, type: "text" },
      { key: "category", label: { en: "Category", es: "Categoría" }, sum: false, type: "text" },
      { key: "reference", label: { en: "Reference", es: "Referencia" }, sum: false, type: "text" },
      { key: "lineType", label: { en: "Line Type", es: "Tipo de Línea" }, sum: false, type: "text" },
      { key: "description", label: { en: "Description", es: "Descripción" }, sum: false, type: "text" },
      { key: "qty", label: { en: "Qty", es: "Cant." }, sum: true, type: "number" },
      { key: "rate", label: { en: "Rate", es: "Tarifa" }, sum: false, type: "money" },
      { key: "amount", label: { en: "Amount", es: "Monto" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
    global: [
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "vendor", label: { en: "Vendor", es: "Proveedor" }, sum: false, type: "text" },
      { key: "category", label: { en: "Category", es: "Categoría" }, sum: false, type: "text" },
      { key: "reference", label: { en: "Reference", es: "Referencia" }, sum: false, type: "text" },
      { key: "amount", label: { en: "Amount", es: "Monto" }, sum: true, type: "money" },
      { key: "paid", label: { en: "Paid", es: "Pagado" }, sum: true, type: "money" },
      { key: "balance", label: { en: "Balance", es: "Saldo" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
  },
  invoices: {
    hasDetailToggle: true,
    hasStatusFilter: true,
    detail: [
      { key: "puNumber", label: { en: "PU #", es: "PU #" }, sum: false, type: "text" },
      { key: "poNumber", label: { en: "PO #", es: "PO #" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "clientName", label: { en: "Client", es: "Cliente" }, sum: false, type: "text" },
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "lineType", label: { en: "Line Type", es: "Tipo de Línea" }, sum: false, type: "text" },
      { key: "description", label: { en: "Description", es: "Descripción" }, sum: false, type: "text" },
      { key: "qty", label: { en: "Qty", es: "Cant." }, sum: true, type: "number" },
      { key: "rate", label: { en: "Rate", es: "Tarifa" }, sum: false, type: "money" },
      { key: "amount", label: { en: "Amount", es: "Monto" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
    global: [
      { key: "puNumber", label: { en: "PU #", es: "PU #" }, sum: false, type: "text" },
      { key: "poNumber", label: { en: "PO #", es: "PO #" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "clientName", label: { en: "Client", es: "Cliente" }, sum: false, type: "text" },
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "total", label: { en: "Total", es: "Total" }, sum: true, type: "money" },
      { key: "paid", label: { en: "Paid", es: "Pagado" }, sum: true, type: "money" },
      { key: "balance", label: { en: "Balance", es: "Saldo" }, sum: true, type: "money" },
      { key: "status", label: { en: "Status", es: "Estado" }, sum: false, type: "text" },
    ],
  },
  /* Arturo, 2026-09-17: "necesito un payable and receivables con los
     periodos standar de vencimiento, current, 7 days, 14 days, 21
     days, 30 days, longer... debe incluir los saldos pendientes no
     montos originales" — followed later the same day by "el aging
     report esta mal, me da todas [las facturas] en un solo renglon,
     debe ser cada una en un renglon no un summary": ONE ROW PER OPEN
     BILL/INVOICE (the standard "aging detail" layout), never
     collapsed into one summary row per entity — the record's balance
     lands in exactly one of the 6 bucket columns (days PAST DUE from
     its own dueDate), the rest blank. Global only in the sense that
     there's no separate Detail/Global toggle or Status filter (a
     bucket of $0 balance simply doesn't show up, same as "fully
     paid") — but "global" never meant "one row per entity". */
  aging: {
    hasDetailToggle: false,
    hasStatusFilter: false,
    /* Arturo, 2026-09-17: "agrega el numero de factura del shipper y el
       numero de proforma, solo para shippers" / "en los reportes de
       clientes incluye su PO number" — Payables (shipper) rows show
       Shipper Invoice #/Proforma #; Receivables (client) rows show
       PO # instead — neither concept applies to the other side. */
    /* Arturo, 2026-09-17: "aging no se muestra los headers como en
       xero, debe ser identico" (pasted Xero's own "Accounts Receivable
       Aging Detail" column row: Current / 1-30 Days / 31-60 Days /
       61-90 Days / Older / Total) — bucket boundaries and labels now
       match Xero's exactly, replacing the earlier 7-day-increment
       buckets. */
    columnsPayables: [
      { key: "entityName", label: { en: "Supplier / Client", es: "Proveedor / Cliente" }, sum: false, type: "text" },
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "shipperInvoice", label: { en: "Shipper Invoice #", es: "Factura del Exportador #" }, sum: false, type: "text" },
      { key: "proforma", label: { en: "Proforma #", es: "Proforma #" }, sum: false, type: "text" },
      { key: "date", label: { en: "Invoice Date", es: "Fecha de Factura" }, sum: false, type: "text" },
      { key: "dueDate", label: { en: "Due Date", es: "Fecha de Vencimiento" }, sum: false, type: "text" },
      { key: "current", label: { en: "Current", es: "Corriente" }, sum: true, type: "money" },
      { key: "d30", label: { en: "1 - 30 Days", es: "1 - 30 Días" }, sum: true, type: "money" },
      { key: "d60", label: { en: "31 - 60 Days", es: "31 - 60 Días" }, sum: true, type: "money" },
      { key: "d90", label: { en: "61 - 90 Days", es: "61 - 90 Días" }, sum: true, type: "money" },
      { key: "older", label: { en: "Older", es: "Más Antiguo" }, sum: true, type: "money" },
      { key: "totalBalance", label: { en: "Total", es: "Total" }, sum: true, type: "money" },
    ],
    columnsReceivables: [
      { key: "entityName", label: { en: "Supplier / Client", es: "Proveedor / Cliente" }, sum: false, type: "text" },
      /* Arturo, 2026-09-17: "en lugar de PU en aging report, debe
         mostrar Invoice para los clientes... los de four seasons deben
         mostrar el numero propio de invoice que lleva sweetleaf, todos
         los demas clientes muestran el numero de PU de ground fresh
         pero debe decir Invoice porque en Ground-Fresh PU e Invoice es
         el mismo numero, en Sweetleaf PU = GFI Invoice, invoice = SL
         invoice number" — label says "Invoice" always; the VALUE
         (puNumber key, unchanged for column-sort/export plumbing)
         resolves to the real SweetLeaf invoice # for Four Seasons
         records (via buildAgingRows' slInvoiceByPu map) and to the
         GFI PU # for everyone else, where PU# and Invoice # are the
         same number by design. */
      { key: "puNumber", label: { en: "Invoice", es: "Factura" }, sum: false, type: "text" },
      { key: "poNumber", label: { en: "PO #", es: "PO #" }, sum: false, type: "text" },
      { key: "date", label: { en: "Invoice Date", es: "Fecha de Factura" }, sum: false, type: "text" },
      { key: "dueDate", label: { en: "Due Date", es: "Fecha de Vencimiento" }, sum: false, type: "text" },
      { key: "current", label: { en: "Current", es: "Corriente" }, sum: true, type: "money" },
      { key: "d30", label: { en: "1 - 30 Days", es: "1 - 30 Días" }, sum: true, type: "money" },
      { key: "d60", label: { en: "31 - 60 Days", es: "31 - 60 Días" }, sum: true, type: "money" },
      { key: "d90", label: { en: "61 - 90 Days", es: "61 - 90 Días" }, sum: true, type: "money" },
      { key: "older", label: { en: "Older", es: "Más Antiguo" }, sum: true, type: "money" },
      { key: "totalBalance", label: { en: "Total", es: "Total" }, sum: true, type: "money" },
    ],
  },
  receivedVsSold: {
    hasDetailToggle: false,
    columns: [
      { key: "lot", label: { en: "Lot", es: "Lote" }, sum: false, type: "text" },
      { key: "date", label: { en: "Date", es: "Fecha" }, sum: false, type: "text" },
      { key: "supplierName", label: { en: "Shipper", es: "Shipper" }, sum: false, type: "text" },
      { key: "cases", label: { en: "Cases", es: "Cajas" }, sum: true, type: "number" },
      { key: "received", label: { en: "Received (Cost)", es: "Recibido (Costo)" }, sum: true, type: "money" },
      { key: "sold", label: { en: "Sold (Revenue)", es: "Vendido (Ingreso)" }, sum: true, type: "money" },
      { key: "expensesCharged", label: { en: "Expenses Charged", es: "Gastos Cobrados" }, sum: true, type: "money" },
      { key: "expensesPaid", label: { en: "Expenses Paid", es: "Gastos Pagados" }, sum: true, type: "money" },
      { key: "profit", label: { en: "Profit", es: "Ganancia" }, sum: true, type: "money" },
      { key: "margin", label: { en: "Margin %", es: "Margen %" }, sum: false, type: "number" },
    ],
  },
};

function round2(n) { var v = parseFloat(n); return isNaN(v) ? 0 : Math.round(v * 100) / 100; }
function sumPayments(rec) {
  var sum = ((rec && rec.payments) || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  if (sum > 0) return sum;
  return parseFloat(rec && rec.amountPaid) || 0;
}

/* ======================== 1. Shipments ======================== */
function billReportedPriceIndex(bills) {
  var idx = {};
  (bills || []).forEach(function (b) {
    (b.lines || []).forEach(function (l) {
      var key = b.lot + "|" + (l.productBase || l.productName || "") + "|" + (l.category || "");
      idx[key] = parseFloat(l.reportedPrice) || 0;
    });
  });
  return idx;
}

export function buildShipmentRows(shipments, bills, expenseBills, opts) {
  var detail = !!(opts && opts.detail);
  var priceIdx = billReportedPriceIndex(bills);
  var expenseByLot = detail ? buildExpenseByLot(expenseBills || [], shipments || []) : {};
  /* Live per-lot delivery status (see computeSaleStatusByLot above) —
     opts.invoices/opts.slPassingIndex come from ReportGenerator.js,
     already loaded fresh from Supabase. Attached to every row after
     the fact (see the .map() at the bottom of this function) rather
     than threaded through each of the 4 row-push sites below. */
  var statusByLot = computeSaleStatusByLot(opts && opts.invoices, opts && opts.slPassingIndex);
  var lang = opts && opts.lang;
  var rows = [];
  (shipments || []).forEach(function (s) {
    (s.lines || []).forEach(function (l) {
      var cases = parseInt(l.cases, 10) || 0;
      if (cases <= 0) return;
      var key = s.lot + "|" + (l.productBase || l.productName || "") + "|" + (l.category || "");
      var price = priceIdx[key] != null ? priceIdx[key] : (parseFloat(l.price) || 0);
      if (!detail) {
        rows.push({
          lot: s.lot || "", date: s.dateArrival || s.dateDeparture || "",
          supplierId: s.supplierId || "", supplierName: s.shipperName || "",
          shipperInvoice: s.shipperInvoice || "", proforma: s.proforma || "",
          product: l.productBase || l.productName || "", category: l.category || "",
          cases: cases, pallets: parseInt(l.pallets, 10) || 0,
          purchasePrice: round2(price), total: round2(cases * price),
        });
        return;
      }
      rows.push({
        lot: s.lot || "", date: s.dateArrival || s.dateDeparture || "", supplierId: s.supplierId || "", supplierName: s.shipperName || "",
        shipperInvoice: s.shipperInvoice || "", proforma: s.proforma || "",
        lineType: "Product", description: l.productBase || l.productName || "",
        qty: cases, rate: round2(price), amount: round2(cases * price),
        _groupKey: s.id || s.lot, _groupLabel: "Lot " + (s.lot || ""),
      });
    });
    if (!detail) return;
    /* Fees CHARGED to the shipper on their settlement Bill(s) for this lot. */
    var chargedForMatch = []; /* {desc, amount, used} — for the Expense Paid de-dupe below */
    (bills || []).filter(function (b) { return String(b.lot) === String(s.lot); }).forEach(function (b) {
      (b.fees || []).forEach(function (f) {
        var desc = f.feeType === "__custom__" ? (f.feeCustom || "Other") : (f.feeType || "Other");
        rows.push({
          lot: s.lot || "", date: s.dateArrival || s.dateDeparture || "", supplierId: s.supplierId || "", supplierName: s.shipperName || "",
          shipperInvoice: s.shipperInvoice || "", proforma: s.proforma || "",
          lineType: "Fee Charged", description: desc, qty: f.qty != null ? f.qty : 1, rate: round2(f.rate), amount: round2(f.amount),
          _groupKey: s.id || s.lot, _groupLabel: "Lot " + (s.lot || ""),
        });
        chargedForMatch.push({ desc: desc.toLowerCase(), amount: round2(f.amount), used: false });
      });
    });
    /* Actual costs GFI PAID vendors (Expense Bills) attributed to this
       lot. Arturo, 2026-09-17: "porque veo fees duplicados en varios
       embarques?" — investigated against real data (lot 58043): these
       are genuinely 2 different figures (what's CHARGED to the
       shipper vs what GFI actually PAID the vendor), not a bug — but
       for pass-through fees with no markup (e.g. customs/broker fees)
       the two numbers are identical, so the row looked like a literal
       duplicate. Skip the Expense Paid row ONLY when it exactly
       matches an unconsumed Fee Charged row (same description, same
       amount) — nothing new to report there. When GFI marks the fee
       up (e.g. Warehouse: cost $18/pallet charged at $24/pallet), the
       amounts differ and BOTH rows still show, surfacing the real
       markup profit. */
    (expenseByLot[s.lot] || []).forEach(function (exp) {
      var desc = exp.feeType || exp.category || "Other";
      var amount = round2(exp.actualCost);
      var match = chargedForMatch.find(function (c) { return !c.used && c.desc === desc.toLowerCase() && c.amount === amount; });
      if (match) { match.used = true; return; }
      rows.push({
        lot: s.lot || "", date: s.dateArrival || s.dateDeparture || "", supplierId: s.supplierId || "", supplierName: s.shipperName || "",
        shipperInvoice: s.shipperInvoice || "", proforma: s.proforma || "",
        lineType: "Expense Paid", description: desc, qty: "", rate: "", amount: amount,
        _groupKey: s.id || s.lot, _groupLabel: "Lot " + (s.lot || ""),
      });
    });
  });
  return rows.map(function (r) { var key = statusByLot[String(r.lot)]; return { ...r, status: ssLabel(key, lang), statusKey: key || "" }; });
}

var SHIPMENT_STATUS_ORDER = ["goodDelivery", "partialProtection", "protection", "pas", "pendingDelivery", "pendingPickUp", ""];
function pctOf(n, total) { return total > 0 ? round2((n / total) * 100) : 0; }
function statusLabelOrNone(key, lang) { return key ? ssLabel(key, lang) : (lang === "es" ? "Sin status" : "No status"); }

/* Groups a flat list of { statusKey, cases, pallets } entries into the
   ordered/labeled/percentaged row shape shared by the overall totals
   and each per-product breakdown below. Percentages are always of the
   TOTAL passed in for that same grouping (grand total for the overall
   rows, that product's own total for a per-product breakdown) — never
   cross-denominated between the two. */
function summarizeByStatus(entries, totalCases, totalPallets, lang) {
  var byStatus = {};
  entries.forEach(function (e) {
    if (!byStatus[e.statusKey]) byStatus[e.statusKey] = { cases: 0, pallets: 0 };
    byStatus[e.statusKey].cases += e.cases;
    byStatus[e.statusKey].pallets += e.pallets;
  });
  return SHIPMENT_STATUS_ORDER.filter(function (k) { return byStatus[k]; }).map(function (key) {
    var t = byStatus[key];
    return {
      statusKey: key, label: statusLabelOrNone(key, lang),
      cases: t.cases, pallets: t.pallets,
      casePct: pctOf(t.cases, totalCases), palletPct: pctOf(t.pallets, totalPallets),
    };
  });
}

/* Status breakdown for the Shipments report (2026-09-23, Arturo: "se
   puede agregar a este mismo reporte totales de los distntos status
   tanto cajas como pallets con porcentaje de acuerdo al total
   recibido?", followed immediately by "y por porducto" — broken down
   by product too, each with its own % of THAT product's total
   received, not the grand total across all products). Computed
   straight from the filtered shipments' own lines (cases/pallets
   actually received on the truck), grouped by the SAME live per-lot
   status as the Status column — independent of Detail Level/Group By,
   since collapsing rows by period or into fee/expense lines would
   make a per-status case/pallet count meaningless. */
export function buildShipmentStatusTotals(shipments, invoices, slPassingIndex, lang) {
  var statusByLot = computeSaleStatusByLot(invoices, slPassingIndex);
  var allEntries = [];
  var byProductEntries = {}; // product name -> entries[]
  var grandCases = 0, grandPallets = 0;
  var productTotals = {}; // product name -> { cases, pallets }
  (shipments || []).forEach(function (s) {
    var key = statusByLot[String(s.lot)] || "";
    (s.lines || []).forEach(function (l) {
      var cases = parseInt(l.cases, 10) || 0;
      var pallets = parseInt(l.pallets, 10) || 0;
      if (cases <= 0 && pallets <= 0) return;
      var product = l.productBase || l.productName || (lang === "es" ? "Sin producto" : "No product");
      var entry = { statusKey: key, cases: cases, pallets: pallets };
      allEntries.push(entry);
      (byProductEntries[product] || (byProductEntries[product] = [])).push(entry);
      if (!productTotals[product]) productTotals[product] = { cases: 0, pallets: 0 };
      productTotals[product].cases += cases;
      productTotals[product].pallets += pallets;
      grandCases += cases;
      grandPallets += pallets;
    });
  });
  var overallRows = summarizeByStatus(allEntries, grandCases, grandPallets, lang);
  var byProduct = Object.keys(byProductEntries).sort().map(function (product) {
    var pt = productTotals[product];
    return {
      product: product, totalCases: pt.cases, totalPallets: pt.pallets,
      rows: summarizeByStatus(byProductEntries[product], pt.cases, pt.pallets, lang),
    };
  });
  return { rows: overallRows, totalCases: grandCases, totalPallets: grandPallets, byProduct: byProduct };
}

/* Resolves the bucket a shipment's chart bar falls into, for whichever
   granularity Arturo picked (2026-09-23: "que me de la opcion de
   seleccionar si la grafica la quiero por semana o por mes o gasta por
   temporada si quiero cpomparar varias temporadas"). `seasons` is the
   raw season records array (needed by resolveSeasonId's date-range
   fallback); `seasonNameById` is a pre-resolved id->display-name map
   (built in ReportGenerator.js the same way its own periodLabel/season
   filter already resolve names — this file never reads storage
   itself). A shipment with no resolvable season (periodBy="season" but
   its date falls outside every season's range) is skipped — nothing
   sensible to chart it under. */
function chartPeriodOf(dateStr, periodBy, seasons, seasonNameById, lang) {
  if (periodBy === "month") {
    var mk = getMonthKey(dateStr);
    if (!mk) return null;
    /* Short axis label = 3-letter month abbreviation, no year — same
       "keep it short so bars don't overlap" reasoning as week below. */
    var monthFull = formatPeriodLabel(mk, "month", lang);
    return { key: mk, label: monthFull, shortLabel: monthFull.split(" ")[0].slice(0, 3) };
  }
  if (periodBy === "season") {
    var sid = resolveSeasonId(seasons, dateStr, null);
    if (!sid) return null;
    var seasonLabel = (seasonNameById && seasonNameById[sid]) || sid;
    return { key: sid, label: seasonLabel, shortLabel: seasonLabel };
  }
  var wk = getISOWeekKey(dateStr);
  if (!wk) return null;
  /* 2026-09-23, Arturo: "las semanas se ven encimadas, solo por el
     numero de semana con orientacion vertical para que no se
     sobrepongan" — the axis label under each bar drops the "Sem "/
     "Wk "/year and shows just the week number ("38"), and the
     exporter renders it in a vertical writing-mode so even a full row
     of narrow weekly bars never overlaps. */
  return { key: wk, label: formatPeriodLabel(wk, "week", lang), shortLabel: wk.split("-W")[1] || wk };
}

/* Sorts period buckets ascending, same "oldest first" rule as the rest
   of this module. Week/month keys ("2026-W38"/"2026-09") already sort
   correctly as plain strings; season ids don't (they're arbitrary,
   usually Date.now()-based), so those sort by each season's own
   startDate instead, falling back to the id string if a season's
   startDate is missing. */
function sortPeriodKeys(keys, periodBy, seasons) {
  if (periodBy !== "season") return keys.sort();
  var startById = {};
  (seasons || []).forEach(function (s) { startById[String(s.id)] = s.startDate || ""; });
  return keys.sort(function (a, b) {
    var sa = startById[a] || "", sb = startById[b] || "";
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

function chartRowsFromByPeriod(byPeriod, labelByKey, shortLabelByKey, periodBy, seasons, lang) {
  return sortPeriodKeys(Object.keys(byPeriod), periodBy, seasons).map(function (key) {
    var statusMap = byPeriod[key];
    var total = 0;
    Object.keys(statusMap).forEach(function (k) { total += statusMap[k]; });
    var segments = SHIPMENT_STATUS_ORDER.filter(function (k) { return statusMap[k]; }).map(function (k) {
      var cases = statusMap[k];
      return { statusKey: k, label: statusLabelOrNone(k, lang), cases: cases, pct: pctOf(cases, total) };
    });
    return { weekKey: key, weekLabel: labelByKey[key] || key, weekLabelShort: shortLabelByKey[key] || labelByKey[key] || key, total: total, segments: segments };
  });
}

/* Stacked-status chart data for the Shipments report (2026-09-23,
   Arturo: "grafico de barras con los colores de cada status acumulados
   por semana es decir semana 39 son su barra de 3 colores indicando
   cuando fue en cada status no con numero sino el color, mas color
   mayor numero por asi decirlo" — followed by "pero debe mostrar una
   barra por cada producto o 2 charts separadas, como mejor convenga",
   and then "que me de la opcion de seleccionar si la grafica la quiero
   por semana o por mes o gasta por temporada si quiero cpomparar
   varias temporadas"). Built as one chart per product (same "All
   Products" + per-product split already used by buildShipmentStatusTotals
   above), grouped by whichever granularity `periodBy` selects (week/
   month/season — see chartPeriodOf). Each bar splits into colored
   segments by the same live per-lot status as everywhere else in this
   report, segment height proportional to cases (not shown as a raw
   number — the color band itself IS the value, per Arturo's own
   framing). Periods sort ascending. */
export function buildShipmentPeriodStatusChart(shipments, invoices, slPassingIndex, lang, periodBy, seasons, seasonNameById) {
  var statusByLot = computeSaleStatusByLot(invoices, slPassingIndex);
  var byPeriodOverall = {}; // periodKey -> statusKey -> cases
  var byProductPeriod = {}; // product -> periodKey -> statusKey -> cases
  var labelByKey = {};
  var shortLabelByKey = {};
  (shipments || []).forEach(function (s) {
    var dateStr = s.dateArrival || s.dateDeparture;
    var period = chartPeriodOf(dateStr, periodBy, seasons, seasonNameById, lang);
    if (!period) return;
    var periodKey = period.key;
    labelByKey[periodKey] = period.label;
    shortLabelByKey[periodKey] = period.shortLabel;
    var statusKey = statusByLot[String(s.lot)] || "";
    (s.lines || []).forEach(function (l) {
      var cases = parseInt(l.cases, 10) || 0;
      if (cases <= 0) return;
      var product = l.productBase || l.productName || (lang === "es" ? "Sin producto" : "No product");
      if (!byPeriodOverall[periodKey]) byPeriodOverall[periodKey] = {};
      byPeriodOverall[periodKey][statusKey] = (byPeriodOverall[periodKey][statusKey] || 0) + cases;
      if (!byProductPeriod[product]) byProductPeriod[product] = {};
      if (!byProductPeriod[product][periodKey]) byProductPeriod[product][periodKey] = {};
      byProductPeriod[product][periodKey][statusKey] = (byProductPeriod[product][periodKey][statusKey] || 0) + cases;
    });
  });
  var overall = chartRowsFromByPeriod(byPeriodOverall, labelByKey, shortLabelByKey, periodBy, seasons, lang);
  var byProduct = Object.keys(byProductPeriod).sort().map(function (product) {
    return { product: product, weeks: chartRowsFromByPeriod(byProductPeriod[product], labelByKey, shortLabelByKey, periodBy, seasons, lang) };
  });
  return { overall: overall, byProduct: byProduct };
}

/* ======================== 2. Bills (shipment settlements) ======================== */
function calcBillSubtotal(bill) {
  var lines = bill.lines || []; var fees = bill.fees || []; var credits = bill.credits || []; var transfers = bill.balanceTransfers || [];
  var billDefaultRate = parseFloat(bill.commissionRate) || 0;
  var grossSales = 0, commAmt = 0;
  lines.forEach(function (l) {
    if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return;
    var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? billDefaultRate : (parseFloat(l.commissionRate) || 0);
    grossSales += lineAmt; commAmt += lineAmt * (lineRate / 100);
  });
  var totalFees = 0; fees.forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var totalCredits = 0; credits.forEach(function (c) { totalCredits += parseFloat(c.amount) || 0; });
  var totalTransfers = 0; transfers.forEach(function (t) { var amt = parseFloat(t.amount) || 0; if (t.type === "credit") totalTransfers += amt; else if (t.type === "debit") totalTransfers -= amt; });
  var subtotal = grossSales - commAmt - totalFees + totalCredits + totalTransfers;
  return { grossSales: grossSales, totalFees: totalFees, totalCredits: totalCredits, commission: commAmt, subtotal: subtotal };
}
function billStatus(bill, subtotal) {
  var paid = sumPayments(bill);
  var balance = subtotal - paid;
  if (bill.status !== "draft" && paid > 0 && balance <= 0.01) return "paid";
  if ((bill.status || "draft") === "draft") return "draft";
  return "pending";
}

export function buildBillRows(bills, opts) {
  var detail = !!(opts && opts.detail);
  var rows = [];
  (bills || []).forEach(function (b) {
    var totals = calcBillSubtotal(b);
    var status = billStatus(b, totals.subtotal);
    var advancesPaid = round2(sumPayments(b));
    var refFields = { shipperInvoice: b.shipperInvoice || "", proforma: b.proforma || "" };
    if (!detail) {
      rows.push({
        lot: b.lot || "", date: b.billDate || "", supplierName: b.shipperName || "", ...refFields,
        grossSales: round2(totals.grossSales), commission: round2(totals.commission), totalFees: round2(totals.totalFees), totalCredits: round2(totals.totalCredits),
        subtotal: round2(totals.subtotal), advancesPaid: advancesPaid, balance: round2(totals.subtotal - advancesPaid), status: status,
      });
      return;
    }
    var billKey = b.id || b.lot; var billLabel = "Lot " + (b.lot || "");
    (b.lines || []).forEach(function (l) {
      if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return;
      var cases = parseInt(l.cases, 10) || 0;
      var rate = parseFloat(l.reportedPrice) || 0;
      rows.push({
        lot: b.lot || "", date: b.billDate || "", supplierName: b.shipperName || "", ...refFields,
        lineType: "Product", description: l.productBase || l.productName || "",
        qty: cases, rate: round2(rate), amount: round2(cases * rate), status: status,
        _groupKey: billKey, _groupLabel: billLabel,
      });
    });
    /* Arturo, 2026-09-17: "el shipment bill solo incluye gastos, no
       incluyes mi comision a mostrar en deducciones" — GFI's own
       commission (already netted out of `subtotal` by
       calcBillSubtotal, same math BillRegistry.js's own settlement
       uses) needs its own visible deduction line, not just fees. */
    /* Arturo, 2026-09-17: "ademas los gastos, los esta sumando" /
       "gastos y todo tipo deducciones son negativos" — Commission,
       Fee, and Advance Paid lines are DEDUCTIONS from what's owed to
       the shipper (only Product is an addition) — shown as negative
       amounts, accounting-statement style, so the per-lot subtotal
       (a naive sum of the amount column) nets out to the real balance
       instead of adding every deduction on top of the product total. */
    if (totals.commission > 0.005) {
      var billRate = parseFloat(b.commissionRate) || 0;
      rows.push({
        lot: b.lot || "", date: b.billDate || "", supplierName: b.shipperName || "", ...refFields,
        lineType: "Commission", description: "GFI Commission (" + billRate + "%)",
        qty: "", rate: "", amount: round2(-totals.commission), status: status,
        _groupKey: billKey, _groupLabel: billLabel,
      });
    }
    (b.fees || []).forEach(function (f) {
      var desc = f.feeType === "__custom__" ? (f.feeCustom || "Other") : (f.feeType || "Other");
      rows.push({
        lot: b.lot || "", date: b.billDate || "", supplierName: b.shipperName || "", ...refFields,
        lineType: "Fee", description: desc, qty: f.qty != null ? f.qty : 1, rate: round2(f.rate),
        amount: round2(-(parseFloat(f.amount) || 0)), status: status,
        _groupKey: billKey, _groupLabel: billLabel,
      });
    });
    /* Arturo, 2026-09-17: advances already paid on a lot must show up
       in the report, not just silently reduce a Balance column the
       viewer has to cross-check by hand — one explicit row per
       recorded payment (b.payments[], the same array BillRegistry.js
       itself sums for its own Balance display). */
    (b.payments || []).forEach(function (p) {
      rows.push({
        lot: b.lot || "", date: p.date || b.billDate || "", supplierName: b.shipperName || "", ...refFields,
        lineType: "Advance Paid", description: p.concept || p.reference || "Payment",
        qty: "", rate: "", amount: round2(-(parseFloat(p.amount) || 0)), status: status,
        /* _sortKey (not b.billDate for the *display* `date` column, which
           stays the real payment date) — a payment's own date can fall
           on a different day than the bill's other lines, and the
           report's general ascending-sort rule sorts by individual row
           date; without this, a payment could land far from its bill's
           other rows after sorting, splitting it out of its own
           per-lot subtotal group. Sorting by the group's own date keeps
           every row of a bill contiguous no matter which day its
           payment posted. */
        _sortKey: b.billDate || p.date || "",
        _groupKey: billKey, _groupLabel: billLabel,
      });
    });
  });
  return rows;
}

/* ======================== 3. Expense Bills ======================== */
var CAT_LABELS = { customs: "Customs", warehouse: "Warehouse", qc: "Quality / Inspection", shipping: "Shipping", repack: "Repack", financing: "Financing", other: "Other" };
function expenseBillStatus(eb) {
  var VALID = { draft: true, awaiting: true, paid: true };
  if (eb.status && VALID[eb.status]) return eb.status;
  var total = parseFloat(eb.amount) || 0; var paid = sumPayments(eb); var balance = total - paid;
  if (balance <= 0 && paid > 0) return "paid";
  if (total > 0) return "awaiting";
  return "draft";
}

export function buildExpenseBillRows(expenseBills, opts) {
  var detail = !!(opts && opts.detail);
  var rows = [];
  (expenseBills || []).forEach(function (eb) {
    var status = expenseBillStatus(eb);
    var catLabel = CAT_LABELS[eb.category] || (eb.category || "");
    if (!detail) {
      var total = parseFloat(eb.amount) || 0; var paid = sumPayments(eb);
      rows.push({
        date: eb.date || "", vendor: eb.vendor || "", category: catLabel, reference: eb.reference || "",
        amount: round2(total), paid: round2(paid), balance: round2(total - paid), status: status,
      });
      return;
    }
    var ebKey = eb.id || eb.reference; var ebLabel = eb.reference || eb.vendor || "";
    (eb.fees || []).forEach(function (f) {
      var desc = f.feeType === "__custom__" ? (f.feeCustom || "Other") : (f.feeType || "Other");
      rows.push({
        date: eb.date || "", vendor: eb.vendor || "", category: catLabel, reference: eb.reference || "",
        lineType: "Fee", description: desc, qty: f.qty != null ? f.qty : 1, rate: round2(f.rate), amount: round2(f.amount), status: status,
        _groupKey: ebKey, _groupLabel: ebLabel,
      });
    });
    /* Arturo, 2026-09-17: "en detailed summary de invoices y todos tipo
       de bills, no incluye los adelantos o pagos que se hayan
       efectuado, debe incluir y la fecha en que se pago o adelanto, y
       descontarlo del balance" — same fix as Bills' Advance Paid rows:
       one row per real payment (eb.payments[], the same array
       ExpenseBillRegistry.js sums for its own Balance), negative
       amount so the per-record subtotal nets to the real balance
       instead of adding it on top. */
    (eb.payments || []).forEach(function (p) {
      rows.push({
        date: p.date || eb.date || "", vendor: eb.vendor || "", category: catLabel, reference: eb.reference || "",
        lineType: "Advance Paid", description: p.concept || p.reference || "Payment",
        qty: "", rate: "", amount: round2(-(parseFloat(p.amount) || 0)), status: status,
        _sortKey: eb.date || p.date || "", _groupKey: ebKey, _groupLabel: ebLabel,
      });
    });
  });
  return rows;
}

/* ======================== 4. Invoices (client sales) ======================== */
function calcInvoiceTotalLocal(inv) {
  var invDefaultRate = parseFloat(inv.commissionRate) || 0;
  var total = 0, commission = 0;
  (inv.lines || []).forEach(function (l) {
    if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return;
    var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? invDefaultRate : (parseFloat(l.commissionRate) || 0);
    total += lineAmt; commission += lineAmt * (lineRate / 100);
  });
  var tFees = 0; (inv.fees || []).forEach(function (fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0; (inv.credits || []).forEach(function (cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  var tTransfers = 0; (inv.balanceTransfers || []).forEach(function (t) { var amt = parseFloat(t.amount) || 0; if (t.type === "credit") tTransfers += amt; else if (t.type === "debit") tTransfers -= amt; });
  return total - commission - tFees + tCredits + tTransfers;
}
function invoiceStatus(inv, total) {
  var paid = sumPayments(inv); var due = total - paid;
  var st = inv.status || "draft";
  if (st === "draft") return "draft";
  if (paid > 0 && due <= 0.01) return "paid";
  if (inv.dueDate && inv.dueDate < new Date().toISOString().slice(0, 10) && due > 0.01) return "overdue";
  return "pending";
}

export function buildInvoiceRows(invoices, opts) {
  var detail = !!(opts && opts.detail);
  var rows = [];
  (invoices || []).forEach(function (inv) {
    var total = calcInvoiceTotalLocal(inv);
    var status = invoiceStatus(inv, total);
    var poNumber = inv.clientRef || "";
    if (!detail) {
      var paid = sumPayments(inv);
      rows.push({
        puNumber: inv.puNumber || "", poNumber: poNumber, date: inv.date || "", clientName: inv.clientName || "", lot: inv.lot || "",
        total: round2(total), paid: round2(paid), balance: round2(total - paid), status: status,
      });
      return;
    }
    var invKey = inv.id || inv.puNumber; var invLabel = "PU " + (inv.puNumber || "");
    (inv.lines || []).forEach(function (l) {
      if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return;
      var cases = parseInt(l.cases, 10) || 0; var rate = parseFloat(l.salePrice) || 0;
      rows.push({
        puNumber: inv.puNumber || "", poNumber: poNumber, date: inv.date || "", clientName: inv.clientName || "", lot: inv.lot || l.lot || "",
        lineType: "Product", description: l.productBase || l.productName || "",
        qty: cases, rate: round2(rate), amount: round2(cases * rate), status: status,
        _groupKey: invKey, _groupLabel: invLabel,
      });
    });
    /* Same fix as Bills detail rows above — a Fee is a deduction from
       what the client owes, shown as a negative amount so the per-PU
       subtotal nets out correctly instead of adding fees on top. */
    (inv.fees || []).forEach(function (f) {
      var desc = f.feeType === "__custom__" ? (f.feeCustom || "Other") : (f.feeType || "Other");
      rows.push({
        puNumber: inv.puNumber || "", poNumber: poNumber, date: inv.date || "", clientName: inv.clientName || "", lot: inv.lot || "",
        lineType: "Fee", description: desc, qty: f.qty != null ? f.qty : 1, rate: round2(f.rate), amount: round2(-(parseFloat(f.amount) || 0)), status: status,
        _groupKey: invKey, _groupLabel: invLabel,
      });
    });
    /* Arturo, 2026-09-17: "en detailed summary de invoices... no
       incluye los adelantos o pagos que se hayan efectuado, debe
       incluir y la fecha en que se pago... y descontarlo del balance"
       — same fix as Bills' Advance Paid rows: one row per real
       payment (inv.payments[], the same array InvoiceRegistry.js sums
       for its own Balance), negative amount so the per-PU subtotal
       nets to the real balance instead of adding it on top. */
    (inv.payments || []).forEach(function (p) {
      rows.push({
        puNumber: inv.puNumber || "", poNumber: poNumber, date: p.date || inv.date || "", clientName: inv.clientName || "", lot: inv.lot || "",
        lineType: "Advance Paid", description: p.concept || p.reference || "Payment",
        qty: "", rate: "", amount: round2(-(parseFloat(p.amount) || 0)), status: status,
        _sortKey: inv.date || p.date || "", _groupKey: invKey, _groupLabel: invLabel,
      });
    });
  });
  return rows;
}

/* ======================== Aging (AR/AP) ======================== */
function daysPastDue(dueDate, asOf) {
  if (!dueDate) return 0;
  var due = new Date(dueDate + "T00:00:00").getTime();
  var ref = new Date(asOf + "T00:00:00").getTime();
  if (isNaN(due) || isNaN(ref)) return 0;
  return Math.round((ref - due) / 86400000);
}
/* Arturo, 2026-09-17: "debe ser identico" to Xero's own "Accounts
   Receivable Aging Detail" — Current / 1-30 / 31-60 / 61-90 / Older. */
function agingBucketKey(days) {
  if (days <= 0) return "current";
  if (days <= 30) return "d30";
  if (days <= 60) return "d60";
  if (days <= 90) return "d90";
  return "older";
}
/* One row per OPEN Bill (kind="payables") or Invoice (kind="receivables")
   — never collapsed to one row per entity (Arturo, 2026-09-17: "cada una
   en un renglon no un summary"). OUTSTANDING BALANCE only, placed in
   exactly one of the 5 bucket columns by days past `dueDate` as of
   `asOfDate` (default today) — the rest of that row's bucket columns
   are 0, same as a normal aging-detail report.

   `opts.suppliersById`/`opts.clientsById` ({id: fullName}) — Arturo,
   2026-09-17: "nombre completo no usar nombre corto del cliente/
   shipper" — Bill.shipperName/Invoice.clientName store the SHORT name
   (per this app's own naming convention — see CLAUDE.md's "Short Name
   vs Full Name"), so the entity section header falls back to the full
   legal name from the Supplier/Client catalog when available. */
export function buildAgingRows(records, opts) {
  var kind = (opts && opts.kind) || "payables";
  var asOfDate = (opts && opts.asOfDate) || new Date().toISOString().slice(0, 10);
  var namesById = (kind === "payables" ? (opts && opts.suppliersById) : (opts && opts.clientsById)) || {};
  var rows = [];
  (records || []).forEach(function (rec) {
    var balance, entityName, dueDate, date;
    if (kind === "payables") {
      var totals = calcBillSubtotal(rec);
      balance = round2(totals.subtotal - sumPayments(rec));
      entityName = namesById[rec.supplierId] || rec.shipperName || "";
      dueDate = rec.dueDate || "";
      date = rec.billDate || "";
    } else {
      var total = calcInvoiceTotalLocal(rec);
      balance = round2(total - sumPayments(rec));
      entityName = namesById[rec.clientId] || rec.clientName || "";
      dueDate = rec.dueDate || "";
      date = rec.date || "";
    }
    if (!entityName || balance <= 0.01) return;
    var row = {
      entityName: entityName, date: date, dueDate: dueDate,
      current: 0, d30: 0, d60: 0, d90: 0, older: 0, totalBalance: balance,
    };
    row[agingBucketKey(daysPastDue(dueDate, asOfDate))] = balance;
    if (kind === "payables") { row.lot = rec.lot || ""; row.shipperInvoice = rec.shipperInvoice || ""; row.proforma = rec.proforma || ""; }
    else {
      /* Arturo, 2026-09-17: "los de four seasons deben mostrar el
         numero propio de invoice que lleva sweetleaf... en Sweetleaf
         PU = GFI Invoice, invoice = SL invoice number" — slInvoiceByPu
         ({gfiPuNumber: slInvoiceNumber}) only ever has entries for
         invoices actually copied to SweetLeaf (Four Seasons), so
         everyone else falls through to the plain GFI PU# — which IS
         the invoice number for a Ground-Fresh client, same number. */
      var slInvoiceByPu = (opts && opts.slInvoiceByPu) || {};
      row.puNumber = slInvoiceByPu[rec.puNumber] || rec.puNumber || "";
      row.poNumber = rec.clientRef || "";
    }
    rows.push(row);
  });
  return rows;
}

/* ======================== 5. Received vs Sold (profit) ======================== */
/* One row per distinct lot in scope. Reuses calcLotProfit() as-is (not
   refactored for bulk perf this round — see plan notes). `received` is
   the gross amount reported/paid to the shipper for that lot (summed
   directly from the bill lines here, since calcLotProfit's internal
   repByProd isn't exposed on its return shape — no change made to that
   shared file). */
export function buildReceivedVsSoldRows(lots, billsByLot, shipmentsByLot) {
  var rows = [];
  (lots || []).forEach(function (lot) {
    var lotBills = billsByLot[lot] || [];
    var received = 0;
    lotBills.forEach(function (b) {
      (b.lines || []).forEach(function (l) {
        if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return;
        received += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
      });
    });
    var profit = calcLotProfit(lot);
    var shp = shipmentsByLot[lot];
    var chargedTotal = 0; Object.keys(profit.billFeesByType || {}).forEach(function (k) { chargedTotal += profit.billFeesByType[k]; });
    var paidTotal = 0; Object.keys(profit.actualCostsByType || {}).forEach(function (k) { paidTotal += profit.actualCostsByType[k]; });
    rows.push({
      lot: lot, date: (shp && (shp.dateArrival || shp.dateDeparture)) || "", supplierName: (shp && shp.shipperName) || "",
      cases: profit.cases, received: round2(received), sold: round2(profit.revenue),
      expensesCharged: round2(chargedTotal), expensesPaid: round2(paidTotal),
      profit: round2(profit.total), margin: round2(profit.margin),
    });
  });
  return rows;
}

/* ======================== Grouping ======================== */
/* Shared week/month/season key+label resolution used by both
   groupRows (collapses) and labelRowsByPeriod (doesn't). For season,
   the caller passes a pre-resolved `seasonId`/`seasonLabel` per row
   (via resolveSeasonId/loadSeasons in seasonFilter.js — this file
   never reads storage). */
function periodKeyAndLabel(r, groupBy, lang) {
  if (groupBy === "week") { var wk = getISOWeekKey(r.date); return { key: wk, label: formatPeriodLabel(wk, "week", lang) }; }
  if (groupBy === "month") { var mo = getMonthKey(r.date); return { key: mo, label: formatPeriodLabel(mo, "month", lang) }; }
  if (groupBy === "season") { var sid = r.seasonId || ""; return { key: sid, label: r.seasonLabel || sid }; }
  return { key: "", label: "" };
}

/* groupBy: "none" | "week" | "month" | "season". Sums every column
   flagged sum:true; non-sum columns keep the first row's value (used
   for the group label) — only meaningful for Global/summary reports,
   see ReportGenerator.js's exportColumns for why Detailed mode uses
   labelRowsByPeriod instead of this.
   `entityField`, when the report type sections by Shipper/Client/
   Vendor, is folded into the group key too — 2026-10-07, Arturo:
   "pero me da un subtotal por fecha... necesito un subtotal de todo
   lo de cada entity" — grouping by period ALONE collapsed every
   entity's rows for the same week/month into one merged row (losing
   which entity it even was), which then broke the entity subtotal
   below it too. Period and entity are independent dimensions: with
   both set, each entity still gets its own per-period rows, just
   never merged across entities. */
export function groupRows(rows, groupBy, columns, lang, entityField) {
  if (!groupBy || groupBy === "none") return rows;
  var groups = {}; var order = [];
  (rows || []).forEach(function (r) {
    var kl = periodKeyAndLabel(r, groupBy, lang);
    var entityKey = entityField ? (r[entityField] || "") : "";
    var key = entityKey + "|" + kl.key;
    if (!groups[key]) { groups[key] = { period: kl.label }; order.push(key); columns.forEach(function (c) { groups[key][c.key] = c.sum ? 0 : r[c.key]; }); }
    columns.forEach(function (c) { if (c.sum) groups[key][c.key] += parseFloat(r[c.key]) || 0; });
  });
  return order.map(function (k) { var g = groups[k]; columns.forEach(function (c) { if (c.sum) g[c.key] = round2(g[c.key]); }); return g; });
}

/* Arturo, 2026-09-17: "[las columnas lot/PO/date] deberian desaparecer
   solo si detail level es global (summary) porque si es detailed
   entonces si hay forma de detallar cada embarque" — Detailed mode
   should NOT collapse to one row per period (that would throw away
   the per-record detail the whole point of Detailed mode is to show).
   This just tags every row with its period label (for sorting/display)
   without merging anything — record-identifier columns (Lot, PO#,
   Date, etc.) stay intact and readable per row, and the existing
   per-lot subtotal machinery keeps working unchanged. */
export function labelRowsByPeriod(rows, groupBy, lang) {
  if (!groupBy || groupBy === "none") return rows;
  return (rows || []).map(function (r) {
    var kl = periodKeyAndLabel(r, groupBy, lang);
    return { ...r, period: kl.label };
  });
}

/* ======================== Entity section headers ======================== */
/* Arturo, 2026-09-17: "no es necesario tener una columna de cliente o
   shipper, se puede agregar un renglon por cada cliente arriba de sus
   propios lotes... titulos de columnas y despues el detalle, y asi
   por cada shipper... o sea se segmentan por cliente o shipper o
   proveedor de servicio" — instead of repeating the entity name on
   every row, walk the (already entity-sorted) rows and insert a
   `{_entityHeader:true, entityLabel}` marker row each time the entity
   changes; ReportGenerator.js then drops the entity-name column from
   the visible columns (redundant once every section has its own
   header) and the exporters print the entity name + a repeated set of
   column headers before each section's data rows. A falsy/blank
   entity value (e.g. a `_subtotal` row, whose non-sum columns are
   always "") never triggers a new header — it just rides along inside
   whichever section it's already in.

   Arturo, 2026-09-17, after seeing Xero's own "Accounts Receivable
   Aging Detail" report as the reference: ONE column-header row at the
   very top (as always), then just the bold entity-name row per
   section — column labels are NOT repeated under each entity, only
   the name. */
export function insertEntityHeaderRows(rows, entityField) {
  var out = [];
  var lastEntity = null;
  (rows || []).forEach(function (r) {
    var entity = r[entityField];
    if (entity && entity !== lastEntity) {
      out.push({ _entityHeader: true, entityLabel: entity });
      lastEntity = entity;
    }
    out.push(r);
  });
  return out;
}

/* ======================== Entity subtotals ======================== */
/* 2026-10-07, Arturo: "los reportes de bills invoices etc no me da
   totales por cliente, proveedor, me da un solo total general... la
   vista actual, necesito subtotales por vendor" — the entity-header
   feature above (2026-09-17) already sections rows by entity, but
   only marks where a section STARTS; nothing summed the section
   before moving to the next one, leaving just one grand total for
   the whole report. Walks the same entity-sorted rows (already run
   through insertDetailSubtotals, so a per-lot/bill subtotal row may
   sit inside a section with entityField==="" — rides along, same
   "falsy entity never ends a section" rule insertEntityHeaderRows
   already uses) and appends one summed row per entity, skipping
   those per-lot subtotal/header rows when summing so nothing is
   double-counted (same skip computeGrandTotal already applies).
   Must run BEFORE insertEntityHeaderRows so the new subtotal lands
   inside its own section, right before the next one's header. */
export function insertEntitySubtotals(rows, entityField, columns, lang) {
  if (!entityField || !rows || !rows.length) return rows;
  var labelCol = columns.find(function (c) { return c.type === "text" && c.key !== entityField; }) || columns[0];
  var out = [];
  var i = 0;
  while (i < rows.length) {
    var entity = rows[i][entityField];
    var group = [rows[i]];
    i++;
    while (i < rows.length && (!rows[i][entityField] || rows[i][entityField] === entity)) { group.push(rows[i]); i++; }
    group.forEach(function (r) { out.push(r); });
    if (entity) {
      var sub = { _subtotal: true, _entitySubtotal: true };
      columns.forEach(function (c) {
        if (c.sum) {
          var s = 0;
          group.forEach(function (r) { if (!r._subtotal && !r._entityHeader) s += parseFloat(r[c.key]) || 0; });
          sub[c.key] = round2(s);
        } else {
          sub[c.key] = "";
        }
      });
      if (labelCol) sub[labelCol.key] = (lang === "es" ? "Total — " : "Total — ") + entity;
      out.push(sub);
    }
  }
  return out;
}

/* ======================== Grand total ======================== */
/* Sums every `sum: true` column across the final (possibly grouped)
   row set — Arturo, 2026-09-17: "agrega un total general sumando
   cajas y monto". Returned as a plain object with the same keys as a
   normal row so the exporters can render it as one more row, just
   styled differently (bold/bordered). */
export function computeGrandTotal(rows, columns) {
  var total = {};
  columns.forEach(function (c) { if (c.sum) total[c.key] = 0; });
  (rows || []).forEach(function (r) {
    /* Skip per-group subtotal rows (insertDetailSubtotals) — they are
       already a sum of the detail rows right above them, so folding
       them into the grand total too would double-count every group.
       Also skip entity section-header marker rows (insertEntityHeaderRows)
       — they carry no data at all. */
    if (r._subtotal || r._entityHeader) return;
    columns.forEach(function (c) { if (c.sum) total[c.key] += parseFloat(r[c.key]) || 0; });
  });
  columns.forEach(function (c) { if (c.sum) total[c.key] = round2(total[c.key]); });
  return total;
}

/* ======================== Detail-mode subtotals ======================== */
/* Arturo, 2026-09-17: "cuando sea un detailed report, debe agregar un
   total al final de cada bill, o sea si hay 6 lineas del lote 58046,
   ahora la siguiente linea es la 58047, no subtotaliza por lote" —
   walk consecutive rows sharing the same `_groupKey` (a lot/bill/
   invoice/expense-bill, set by the builders above) and insert one
   summed subtotal row after each group. Only called when Detail Level
   is "Detailed" AND rows aren't already grouped by period (grouping
   already collapses to one row per period, no per-record subtotal
   makes sense there). Rows without `_groupKey` (global mode) pass
   through untouched. A group of a single row gets no subtotal line —
   nothing to sum. */
export function insertDetailSubtotals(rows, columns, lang) {
  if (!rows || !rows.length || !rows[0] || rows[0]._groupKey === undefined) return rows;
  var out = [];
  var i = 0;
  while (i < rows.length) {
    var key = rows[i]._groupKey;
    var group = [];
    while (i < rows.length && rows[i]._groupKey === key) { group.push(rows[i]); i++; }
    group.forEach(function (r) { out.push(r); });
    if (group.length > 1) {
      var sub = { _subtotal: true };
      var labelCol = columns.find(function (c) { return c.type === "text"; });
      columns.forEach(function (c) {
        if (c.sum) {
          var s = 0; group.forEach(function (r) { s += parseFloat(r[c.key]) || 0; });
          sub[c.key] = round2(s);
        } else {
          sub[c.key] = "";
        }
      });
      if (labelCol) sub[labelCol.key] = (lang === "es" ? "Subtotal — " : "Subtotal — ") + (group[0]._groupLabel || "");
      out.push(sub);
    }
  }
  return out;
}
